"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import { trackAppInsightsException } from "@/lib/app-insights-client";
import { type ChunkFailureSignal, decideChunkRecovery, getSessionStorage, isChunkLoadFailure, readLastChunkReload, recordChunkReload } from "@/lib/chunk-load-recovery";
const TOAST_ID = "chunk-reload-guard";

/**
 * Free-text controls whose contents a reload would discard. Anything missed
 * here only means the guard reloads instead of asking, which is the intended
 * default — so the list stays narrow rather than trying to be exhaustive.
 */
const TEXT_INPUT_SELECTOR = ["textarea", 'input[type="text"]', 'input[type="search"]', "input:not([type])", '[contenteditable="true"]'].join(", ");

/** Whether a reload right now would throw away something the user typed. */
function hasUnsavedInput(): boolean {
  try {
    for (const element of document.querySelectorAll(TEXT_INPUT_SELECTOR)) {
      const text = element instanceof HTMLTextAreaElement || element instanceof HTMLInputElement ? element.value : element.textContent ?? "";
      if (text.trim().length > 0) {
        return true;
      }
    }
    return false;
  } catch {
    // Never silently discard input on the strength of a failed check.
    return true;
  }
}
function signalFromErrorEvent(event: Event): ChunkFailureSignal | null {
  // A `<script>`/`<link>` that failed to load fires a bare Event on the
  // element itself; only the capturing listener sees it.
  const {
    target
  } = event;
  if (target instanceof HTMLScriptElement) {
    return {
      resource: true,
      url: target.src
    };
  }
  if (target instanceof HTMLLinkElement) {
    return {
      resource: true,
      url: target.href
    };
  }
  if (event instanceof ErrorEvent) {
    return {
      message: event.message,
      name: (event.error as Error | undefined)?.name,
      url: event.filename
    };
  }
  return null;
}
function signalFromRejection(event: PromiseRejectionEvent): ChunkFailureSignal {
  const {
    reason
  } = event;
  if (reason instanceof Error) {
    return {
      message: reason.message,
      name: reason.name
    };
  }
  return {
    message: typeof reason === "string" ? reason : ""
  };
}

/**
 * Recovers a tab whose build was replaced under it by a deploy.
 *
 * Every deploy strands the `/_next/static/` chunks of the build that open tabs
 * are still running, so the next lazily-loaded route or component fails to
 * load and React renders an error boundary in its place — most visibly, a chat
 * message that renders "This message couldn't be displayed" instead of its
 * file card or tool output. Nothing about that state is recoverable in-page
 * (see `lib/chunk-load-recovery.ts`), and the app gives no hint that a refresh
 * is all it takes, so users read it as data loss.
 *
 * This listens for the three shapes a stranded chunk takes — a script parse
 * error, a failed resource load, and a rejected `import()` — and reloads onto
 * the current build, falling back to a refresh prompt when reloading unasked
 * would be destructive or risk a loop.
 *
 * Mounted at the frontend root so it covers every route, not just chat.
 */
export function ChunkReloadGuard() {
  const $ = _c(3);
  const handled = useRef(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = signal => {
      if (handled.current || !isChunkLoadFailure(signal)) {
        return;
      }
      handled.current = true;
      const storage = getSessionStorage();
      const now = Date.now();
      let action = decideChunkRecovery({
        lastReloadAt: readLastChunkReload(storage),
        now,
        hasUnsavedInput: hasUnsavedInput()
      });
      if (action === "reload" && !recordChunkReload(storage, now)) {
        action = "prompt";
      }
      trackAppInsightsException(new Error(`Stranded build asset: ${signal.message || signal.url || "unknown"}`), {
        source: "chunk_load_guard",
        action,
        assetUrl: signal.url,
        errorName: signal.name
      });
      if (action === "reload") {
        window.location.reload();
        return;
      }
      toast.info("A new version of Magnus is available", {
        id: TOAST_ID,
        description: "Refresh to finish loading this page.",
        duration: Number.POSITIVE_INFINITY,
        action: {
          label: "Refresh",
          onClick: _temp
        }
      });
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const recover = t0;
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const onError = event => {
        const signal_0 = signalFromErrorEvent(event);
        if (signal_0) {
          recover(signal_0);
        }
      };
      const onRejection = event_0 => {
        recover(signalFromRejection(event_0));
      };
      window.addEventListener("error", onError, true);
      window.addEventListener("unhandledrejection", onRejection);
      return () => {
        window.removeEventListener("error", onError, true);
        window.removeEventListener("unhandledrejection", onRejection);
      };
    };
    t2 = [recover];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  return null;
}
function _temp() {
  return window.location.reload();
}